import { useState, type FormEvent } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useLang } from '../../../i18n';
import { useToast } from '../../../components/Toast';
import { patch } from '../../../services/api';
import Card from '../../../components/Card';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Button from '../../../components/Button';
import type { LocaleCode } from '../../../types';

const TZ_OPTIONS = [
  { value: 'Asia/Tashkent', label: 'Asia/Tashkent (UTC+5)' },
  { value: 'Asia/Almaty', label: 'Asia/Almaty (UTC+6)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },
  { value: 'UTC', label: 'UTC' },
];

const LOCALE_OPTIONS: { value: LocaleCode; label: string }[] = [
  { value: 'uz_lat', label: "O'zbekcha (Lotin)" },
  { value: 'uz_cyr', label: 'Ўзбекча (Кирилл)' },
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
];

export default function ProfileMain() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const toast = useToast();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [locale, setLocale] = useState<LocaleCode>(user?.default_locale || 'ru');
  const [tz, setTz] = useState('Asia/Tashkent');
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await patch('/me/', { full_name: fullName, default_locale: locale, default_timezone: tz });
      await refresh();
      toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="lg">
      <form className="profile-main__form" onSubmit={onSubmit}>
        <Input
          label={t('profile.full_name')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />
        <Select
          label={t('profile.locale')}
          value={locale}
          onChange={(v) => setLocale(v as LocaleCode)}
          options={LOCALE_OPTIONS}
        />
        <Select
          label={t('profile.timezone')}
          value={tz}
          onChange={setTz}
          options={TZ_OPTIONS}
        />
        <div className="profile-main__actions">
          <Button type="submit" variant="primary" size="md" loading={saving}>
            {t('profile.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
